import {
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';
import { forkJoin } from 'rxjs';

import { ApiErrorResponse } from '../../interfaces/error.interface';
import {
	IProfessionalProfile,
	IProfessionalProfileUpdate,
} from '../../interfaces/professionalprofile.interface';
import { ITaxonomyGroupContext } from '../../interfaces/search.interface';
import { GlobalService } from '../../services/global.service';
import { SearchService } from '../../services/search.service';
import { TaxonomyService } from '../../services/taxonomy.service';
import { UserService } from '../../services/user.service';

const REASONS_LIMIT = 8;
const SPECIALITIES_LIMIT = 3;
const MIN_TEXT_LENGTH = 80;

@Component({
	selector: 'app-my-account-professional',
	templateUrl: './professional.component.html',
	styleUrls: ['./professional.component.scss'],
})
export class MyAccountProfessionalComponent implements OnInit {
	@Output() unsavedChanges = new EventEmitter<boolean>();
	@Output() userUpdated = new EventEmitter();

	@ViewChildren('textareaSummary') textareaSummary: {
		first: { focus: () => void; onBlur: () => void };
	};

	@ViewChildren('textareaDescription') textareaDescription: {
		first: { focus: () => void; onBlur: () => void };
	};

	isLoading = true;
	isSaving = false;
	isDeletingVideo = false;

	isOpenVideoModal = false;
	modalData = {};

	videoEmbedUrl: SafeResourceUrl = '';
	otherTags = [];
	reasons = [];
	specialities = [];
	spokenLanguages = [];

	data: IProfessionalProfile = {
		spokenLanguages: [],
		tags: [],
	} as IProfessionalProfile;

	specialitiesOptions = [];
	reasonsOptions = [];
	languagesOptions = [];
	professionalTypes: ZkIOption[];

	constructor(
		public globalService: GlobalService,
		public user: UserService,
		public translate: TranslateService,
		public notifications: ZkNotificationService,
		public taxonomy: TaxonomyService,
		public search: SearchService,
		private sanitizer: DomSanitizer
	) {
		this.unsavedChanges.emit(false);
	}

	isMobileDevice(): boolean {
		return this.globalService.isMobileDevice();
	}

	arrayToObject(array: { [key: string]: any }[]): { [key: string]: any } {
		return array.reduce((obj, item: any) => ({ ...obj, [item.id]: item }), {});
	}

	getDropdownValues(): void {
		this.reasons = this.data.tags
			.filter(({ id }) => id.indexOf('reason:') > -1)
			.map(({ id }) => id);

		this.specialities = this.data.tags
			.filter(({ id }) => id.indexOf('specialty:') > -1)
			.map(({ id }) => id);

		this.otherTags = this.data.tags
			.filter(
				({ id }) =>
					id.indexOf('reason:') === -1 && id.indexOf('specialty:') === -1
			)
			.map(({ id }) => id);

		this.spokenLanguages = this.data.spokenLanguages.map(({ id }) => id);
	}

	getYouTubeVideoId(url): string {
		const regExp =
			/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
		const match = url.match(regExp);

		return match && match[2].length === 11 ? match[2] : null;
	}

	getYoutubeEmbedUrl(): void {
		if (!this.data.videoUrl) return;
		this.videoEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
			'https://www.youtube.com/embed/' +
				this.getYouTubeVideoId(this.data.videoUrl)
		);
	}

	async ngOnInit() {
		this.data = (await this.user.getProfileData()) as any;
		this.getYoutubeEmbedUrl();
		this.professionalTypes = this.getProfessionalTypes();
		const spokenLanguages = this.arrayToObject(this.data.spokenLanguages);
		const tags = this.arrayToObject(this.data.tags);

		forkJoin([
			this.taxonomy.getTaxonomyList('specialty'),
			this.taxonomy.getTaxonomyGroup(ITaxonomyGroupContext.ProfessionalReason),
			this.search.getLanguages(),
		]).subscribe(([specialities, reasons, languages]) => {
			this.specialitiesOptions = specialities.map(({ id, name }) => ({
				value: id,
				label: name,
				isSelected: tags[id] ? true : false,
			}));

			this.reasonsOptions = reasons.map(({ taxonomyId, name }) => ({
				value: taxonomyId,
				label: name,
				isSelected: tags[taxonomyId] ? true : false,
			}));

			this.languagesOptions = languages.map(({ id, name }) => ({
				value: id,
				label: name,
				isSelected: spokenLanguages[id] ? true : false,
			}));
			this.getDropdownValues();
			this.isLoading = false;
		});
	}

	getProfessionalTypes(): ZkIOption[] {
		const types = [
			'psychologist',
			'psychoanalyst',
			'therapist',
			'coach',
			'nutritionist',
		];

		return types.map((type) => ({
			value: type,
			label: this.translate.instant(
				'MY_ACCOUNT.PROFESSIONAL.FORM.TYPE.OPTIONS.' + type.toUpperCase()
			),
		}));
	}

	deleteVideo() {
		this.isDeletingVideo = true;
		this.user.deleteProfileVideo().subscribe(() => {
			this.data.videoUrl = '';
			this.videoEmbedUrl = '';
			this.isOpenVideoModal = false;
		});
	}

	changeReasons(value: ZkIOption[]) {
		if (this.reasons.length >= REASONS_LIMIT && value.length > REASONS_LIMIT) {
			this.reasonsOptions = this.reasonsOptions.map((option) =>
				this.reasons.indexOf(option.value) > -1
					? option
					: {
							...option,
							isSelected: false,
					  }
			);

			this.showError(
				this.translate.instant('MY_ACCOUNT.PROFESSIONAL.ERRORS.REASONS_LIMIT')
			);
		} else {
			this.reasons = value.map((option) => option.value);
		}
	}

	changeSpecialities(value: ZkIOption[]) {
		if (
			this.specialities.length >= SPECIALITIES_LIMIT &&
			value.length > SPECIALITIES_LIMIT
		) {
			this.specialitiesOptions = this.specialitiesOptions.map((option) =>
				this.specialities.indexOf(option.value) > -1
					? option
					: {
							...option,
							isSelected: false,
					  }
			);

			this.showError(
				this.translate.instant(
					'MY_ACCOUNT.PROFESSIONAL.ERRORS.SPECIALITIES_LIMIT'
				)
			);
		} else {
			this.specialities = value.map((option) => option.value);
		}
	}

	onChangeForm(
		field: string,
		value: Partial<ZkIOption> | Partial<ZkIOption>[] | string
	): void {
		switch (field) {
			case 'spokenLanguages':
				this.spokenLanguages = (<ZkIOption[]>value).map(
					(option) => option.value
				);
				break;

			case 'reasons':
				this.changeReasons(value as ZkIOption[]);
				break;

			case 'specialities':
				this.changeSpecialities(value as ZkIOption[]);
				break;

			case 'summary':
				this.data.summary = value['value'];
				break;

			case 'description':
				this.data.description = value['value'];
				break;

			default:
				this.data[field] = value;
				break;
		}

		this.unsavedChanges.emit(true);
	}

	showError(errorKey: string): void {
		this.notifications.showError(this.translate.instant(errorKey));
	}

	focusTextarea(element: string): void {
		if (this[element].first && this[element].first.focus) {
			this[element].first.onBlur();
			this[element].first.focus();
		}
	}

	validateForm(): string | boolean {
		if (this.spokenLanguages.length === 0) {
			return 'REQUIRED_LANGUAGE';
		}

		if (this.reasons.length === 0) {
			return 'REQUIRED_REASON';
		}

		if (this.reasons.length > REASONS_LIMIT) {
			return 'REASONS_LIMIT';
		}

		if (this.specialities.length > SPECIALITIES_LIMIT) {
			return 'SPECIALITIES_LIMIT';
		}

		if (this.specialities.length === 0) {
			return 'REQUIRED_SPECIALITY';
		}

		if (!this.data.summary) {
			this.focusTextarea('textareaSummary');
			return 'REQUIRED_SUMMARY';
		}

		if (this.data.summary.length < MIN_TEXT_LENGTH) {
			this.focusTextarea('textareaSummary');
			return true;
		}

		if (!this.data.description) {
			this.focusTextarea('textareaDescription');
			return 'REQUIRED_DESCRIPTION';
		}

		if (this.data.description.length < MIN_TEXT_LENGTH) {
			this.focusTextarea('textareaDescription');
			return true;
		}

		if (!this.data.signatureUrl) {
			return 'REQUIRED_SIGNATURE';
		}
	}

	saveForm(): void {
		const error = this.validateForm();
		if (error) {
			if (typeof error === 'string') {
				return this.showError(
					this.translate.instant('MY_ACCOUNT.PROFESSIONAL.ERRORS.' + error)
				);
			}
			return;
		}

		if (!this.isSaving) {
			this.isSaving = true;

			const data = {
				spokenLanguages: this.spokenLanguages,
				tags: this.otherTags.concat(this.reasons).concat(this.specialities),
				summary: this.data.summary,
				description: this.data.description,
			} as IProfessionalProfileUpdate;

			this.user.updateProfileData(data, '/professional').subscribe(
				async () => {
					this.isSaving = false;
					this.unsavedChanges.emit(false);
					this.updateLocalData();
					this.notifications.showSuccess(
						this.translate.instant('MESSAGES.SAVEDSUCCESSFULLY')
					);
					this.userUpdated.emit();
				},
				(error: ApiErrorResponse) => {
					this.isSaving = false;
					this.notifications.showError(
						error.toString(),
						this.translate.instant(
							'MY_ACCOUNT.PROFESSIONAL.ERRORS.GENERIC_TITLE'
						)
					);
				}
			);
		}
	}

	updateLocalData(): void {
		this.user
			.getProfessionalProfileData(this.data.id)
			.subscribe((user: IProfessionalProfile) => {
				if (user) {
					this.user.saveData({
						...user,
					});
				}
			});
	}

	onUpdateSignature() {
		this.updateLocalData();
		this.userUpdated.emit();
	}
}
